import { createContext, useContext, useEffect, useState } from "react";
import { auth, db, onAuthStateChanged } from "../utilities/firebaseConfig";
import { get, onValue, push, ref, set } from "firebase/database";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  // Function to mark notification as read
  const markAsRead = async (notificationId) => {
    if (!user?.uid) return;
    await set(ref(db, `notifications/${user.uid}/${notificationId}/read`), true);
  };

  // Function to send notifications
  const sendNotification = async (recipientId, notificationData) => {
    const notificationsRef = ref(db, `notifications/${recipientId}`);
    const newNotificationRef = push(notificationsRef);
    await set(newNotificationRef, {
      ...notificationData,
      timestamp: Date.now(),
      read: false
    });
  };

  // Load user data and notifications
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        if (!authUser.emailVerified) {
          setNeedsVerification(true);
        } else {
          setNeedsVerification(false);
        }

        setLoading(true);

        // Check user role
        const userSnapshot = await get(ref(db, `users/${authUser.uid}`));
        const technicianSnapshot = await get(ref(db, `technicians/${authUser.uid}`));

        if (userSnapshot.exists()) {
          setUser({ ...authUser, ...userSnapshot.val() });
          setRole("user");
        } else if (technicianSnapshot.exists()) {
          setUser({ ...authUser, ...technicianSnapshot.val() });
          setRole("technician");
        } else {
          setUser(null);
          setRole(null);
        }
        setLoading(false);
      } else {
        setUser(null);
        setRole(null);
        setNeedsVerification(false);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // Set up realtime notifications listener
  useEffect(() => {
    if (!user?.uid) return;

    const notificationsRef = ref(db, `notifications/${user.uid}`);
    const unsubscribeNotifications = onValue(notificationsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const notificationList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        })).sort((a, b) => b.timestamp - a.timestamp);
        
        setNotifications(notificationList);
        setUnreadCount(notificationList.filter(n => !n.read).length);
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    });

    return () => unsubscribeNotifications();
  }, [user]);

  useEffect(() => {
    if (needsVerification) {
      navigate("/verify-email");
    }
  }, [needsVerification, navigate]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      role, 
      loading,
      notifications,
      unreadCount,
      markAsRead,
      sendNotification
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}